/**
 * Password sign-in, reset and renewal from the SPA — M-21.3.
 *
 * ── Why these are hand-written and not the generated client ──────────────────────────────────
 *
 * Every other call goes through `createApiClient`, which attaches a bearer token. These are the
 * three calls made when there is no token to attach — that is the whole point of them — and two of
 * them depend on something the generated client has no concept of: **cookies**.
 *
 * `credentials: 'include'` is what makes the refresh cookie travel, and it is easy to leave off and
 * hard to notice: sign-in appears to work, and the session simply ends at the first renewal with
 * nothing to say why. It is written explicitly on every call here for that reason.
 *
 * ── Where the tokens live ────────────────────────────────────────────────────────────────────
 *
 * The access token comes back in the body and goes into the SPA's memory. The refresh token is
 * never seen by this file at all: it is set as an httpOnly cookie by the API and read back by the
 * browser. That asymmetry is the design — the five-minute credential is the one script may hold,
 * and the month-long one is the one it must not.
 */

import { config } from '../config.js';

interface SessionResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
}

/**
 * What the API said went wrong, when it is safe to show.
 *
 * The auth routes answer with one sentence and no detail about which half was wrong, so their
 * `detail` is fit to display. A failure with no readable body falls back to a general sentence
 * rather than surfacing a status code to somebody trying to sign in.
 */
async function readProblem(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    return typeof body.detail === 'string' && body.detail.length > 0 ? body.detail : fallback;
  } catch {
    return fallback;
  }
}

export async function signInWithPassword(
  email: string,
  password: string,
  /** *Keep me signed in*. False makes the refresh cookie die with the browser. */
  remember = true,
): Promise<SessionResponse> {
  const response = await fetch(`${config.apiUrl}/v1/auth/password`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // Without this the Set-Cookie is discarded and the session cannot be renewed.
    credentials: 'include',
    body: JSON.stringify({ email, password, remember }),
  });

  if (response.status === 429) {
    throw new Error('Too many attempts. Wait a moment and try again.');
  }

  if (!response.ok) {
    throw new Error(
      await readProblem(response, 'That email and password do not match an account.'),
    );
  }

  const session = (await response.json()) as SessionResponse;
  storeAccessToken(session);
  markPasswordSession();
  return session;
}

/**
 * Asks for a reset link, and reports nothing about whether one was sent.
 *
 * Resolves for an address that exists and one that does not. The caller must not branch on the
 * result — the screen shows the same confirmation either way, which is what stops this being a way
 * to ask whether somebody holds an account.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await fetch(`${config.apiUrl}/v1/auth/password-reset/request`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
}

export async function confirmPasswordReset(token: string, password: string): Promise<void> {
  const response = await fetch(`${config.apiUrl}/v1/auth/password-reset/confirm`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    /*
     * Two different failures, and the API keeps them apart on purpose: a password the policy
     * refuses is the person's to fix and the message says how, whereas a spent link sends them to
     * ask for another. Merging them is the defect recorded against activation, where a too-short
     * password reported a broken invitation.
     */
    throw new Error(
      await readProblem(response, 'That reset link has expired or has already been used.'),
    );
  }
}

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Returns `null` when there is no session rather than throwing, because "not signed in" is the
 * ordinary state of somebody who has never signed in, and the app calls this on load precisely to
 * find that out.
 */
export async function refreshSession(): Promise<SessionResponse | null> {
  const response = await fetch(`${config.apiUrl}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: '{}',
  });

  if (response.status === 401) return null;

  if (!response.ok) {
    /*
     * A 503 is the provider being unreachable, and it is **not** a sign-out. Throwing here lets the
     * caller retry rather than destroy a session that nothing has said is over — the same
     * distinction `session.tsx` already makes between a failed call and a rejected credential.
     */
    throw new Error('Could not renew the session.');
  }

  const session = (await response.json()) as SessionResponse;
  storeAccessToken(session);
  return session;
}

export async function signOutSession(): Promise<void> {
  await fetch(`${config.apiUrl}/v1/auth/sign-out`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: '{}',
  }).catch(() => {
    // A sign-out that could not reach the API still clears what this browser holds. The session
    // row outlives it, and the next refresh is refused — which is the correct end state anyway.
  });
}

/*
 * The access token, and only in memory.
 *
 * Not `localStorage`: a token there survives the tab, is readable by any script on the origin, and
 * is exactly the thing the httpOnly cookie exists to avoid holding. Five minutes of exposure is the
 * trade being made deliberately.
 */
let accessToken: string | null = null;
let accessTokenExpiresAt = 0;

/**
 * A local note that a password sign-in happened here — 11 Sep 2026.
 *
 * The refresh cookie is httpOnly, which is the point of it: script cannot read it, so script cannot
 * steal it. The cost is that script cannot **ask** whether one exists either, and the SPA has to
 * know on a cold load, because a password sign-in keeps its access token in memory and navigates
 * with a full page load.
 *
 * Teaching the bootstrap to always try the cookie fixed sign-in and introduced a smaller problem:
 * a 401 fired at every anonymous visitor to the sign-in screen, and at everybody who had just
 * signed out — two red lines in the network tab immediately after a clean sign-out, which is not a
 * failure but looks exactly like one.
 *
 * So this marks that a session was started here. **It is not a credential and proves nothing.** The
 * server consults only the httpOnly cookie; forging this buys an attacker one 401. Losing it costs
 * one unnecessary sign-in. A stale one costs a single wasted request and then clears itself, which
 * is why nothing tries to keep it perfectly in step.
 *
 * `localStorage` rather than `sessionStorage` deliberately — unlike the OIDC token beside it this is
 * not a secret, and the session it refers to outlives the tab, so scoping it to one tab would make
 * it wrong in the ordinary case of opening a second one.
 */
const SESSION_MARKER = 'tf.session.password';

export function markPasswordSession(): void {
  try {
    localStorage.setItem(SESSION_MARKER, '1');
  } catch {
    // A browser refusing storage costs a redundant sign-in, never a broken one.
  }
}

export function clearPasswordSessionMark(): void {
  try {
    localStorage.removeItem(SESSION_MARKER);
  } catch {
    // As above: nothing here is load-bearing.
  }
}

/** Whether a cold load should bother asking the API for a renewal. */
export function mayHavePasswordSession(): boolean {
  try {
    return localStorage.getItem(SESSION_MARKER) === '1';
  } catch {
    /*
     * Storage unavailable — a private window, or blocked site data. Answer **true**: the cost of
     * guessing wrong is one 401, and the cost of guessing wrong the other way is somebody who is
     * signed in being told they are not.
     */
    return true;
  }
}

function storeAccessToken(session: SessionResponse): void {
  accessToken = session.accessToken;
  accessTokenExpiresAt = Date.now() + session.expiresIn * 1000;
}

export function currentAccessToken(): string | null {
  return accessToken;
}

export function clearAccessToken(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
}

/**
 * Whether the token should be renewed before the next call.
 *
 * A margin rather than waiting for expiry: a token that expires mid-flight produces a 401 the user
 * sees, and renewing thirty seconds early costs nothing. The margin is a fraction of the token's
 * own life rather than a constant, so it stays sensible if the provider shortens it.
 */
export function accessTokenNeedsRenewal(now: number = Date.now()): boolean {
  if (!accessToken) return true;
  const margin = Math.min(30_000, Math.max(5_000, (accessTokenExpiresAt - now) * 0.2));
  return now + margin >= accessTokenExpiresAt;
}

/*
 * ── Renewal, single-flight — 30 Sep 2026 ──────────────────────────────────────────────────────
 *
 * `accessTokenNeedsRenewal` was written to be consulted before every call and **was never called
 * by anything**. So the five-minute token was handed out until it died, and from that moment every
 * request in the app answered 401: the shell stayed on screen from state loaded at mount, each
 * action failed with "The credential is not valid for this route.", and nothing renewed or
 * redirected. Reported as a JD upload failing; the API log showed twenty-one routes refusing the
 * same credential inside eighty seconds, which is what a dead token looks like rather than a
 * defect in any one route.
 *
 * Single-flight because the fix would otherwise trade one bug for a worse one: a screen opening
 * six queries at once would fire six renewals, and with a rotating refresh cookie the winner
 * invalidates the other five — turning an expiry into a sign-out.
 */
let renewal: Promise<SessionResponse | null> | null = null;

/**
 * Exchanges the refresh cookie for a new access token, at most once at a time.
 *
 * Never throws: "not signed in" and "the API could not be reached" are both answered with `null`,
 * because every caller's next move is the same either way — try the credential it has, and let the
 * server be the one to say no.
 */
export async function renewSession(): Promise<SessionResponse | null> {
  if (!renewal) {
    const started = refreshSession().catch(() => null);
    // Cleared however it settles, so the next expiry starts a fresh attempt rather than resolving
    // instantly from a stale answer.
    renewal = started.finally(() => {
      renewal = null;
    });
  }
  return renewal;
}

/**
 * The token to send with the next call, renewed first where it is about to expire.
 *
 * The order matters. A live token is returned without a network round trip, which is the case
 * almost every time. A token inside its renewal margin — or absent, on a cold load after a
 * password sign-in — is renewed first, because expiring is the *normal* end of a five-minute
 * credential and must not reach the user as a failure. A renewal that comes back with nothing
 * falls through to whatever is held: the session may be over, and the server is the right place
 * for that to be decided.
 */
export async function accessTokenForRequest(): Promise<string | null> {
  if (accessToken && !accessTokenNeedsRenewal()) {
    return accessToken;
  }

  if (mayHavePasswordSession()) {
    const renewed = await renewSession();
    if (renewed) {
      return renewed.accessToken;
    }
  }

  return accessToken;
}
